import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import CheckboxMultiSelect, { MultiSelectOption } from '@/components/ui/CheckboxMultiSelect/CheckboxMultiSelect';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { postAuth, putAuth } from '@/api/ApiCalls';
import { FundGroupDtoIn } from '@/models/in/FundGroupDtoIn';
import { FundGroupDtoOut } from '@/models/out/FundGroupDtoOut';
import Fund from '@/models/Fund';

const BASE_URL = 'management/fund-groups';

interface UserRow {
  email: string;
  firstName?: string;
  lastName?: string;
}

interface EditOrCreateFundGroupDialogProps {
  open: boolean;
  onClose: () => void;
  groupToEdit?: FundGroupDtoIn | null;
  onSuccess: () => void;
}

const EditOrCreateFundGroupDialog = ({ open, onClose, groupToEdit, onSuccess }: EditOrCreateFundGroupDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };
  const isEditing = !!groupToEdit;

  const { data: funds } = useGetAuthApi<Fund[]>('funds');
  const { data: users } = useGetAuthApi<UserRow[]>('users');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [fundIds, setFundIds] = useState<number[]>([]);
  const [userEmails, setUserEmails] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName(groupToEdit?.name ?? '');
      setDescription(groupToEdit?.description ?? '');
      setFundIds(groupToEdit?.fundIds ?? []);
      setUserEmails(groupToEdit?.userEmails ?? []);
      setError(null);
    }
  }, [open, groupToEdit]);

  const fundOptions: MultiSelectOption<number>[] = useMemo(
    () => (funds ?? []).filter((f) => f.id !== -1).map((f) => ({ value: f.id, label: f.name, sublabel: `#${f.id}` })),
    [funds],
  );
  const userOptions: MultiSelectOption<string>[] = useMemo(
    () => (users ?? []).map((u) => ({
      value: u.email,
      label: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email,
      sublabel: u.email,
    })),
    [users],
  );

  const handleSave = async () => {
    if (!name.trim()) {
      setError(t('fundGroups.dialog.nameRequired'));
      return;
    }
    setError(null);
    setIsSubmitting(true);
    const body: FundGroupDtoOut = { name: name.trim(), description, fundIds, userEmails };
    try {
      if (isEditing) {
        await putAuth<FundGroupDtoIn, object, FundGroupDtoOut>(`${BASE_URL}/${groupToEdit!.id}`, headers, {}, body);
      } else {
        await postAuth<FundGroupDtoIn, FundGroupDtoOut>(BASE_URL, headers, body);
      }
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      setError(message.includes('already exists') ? t('fundGroups.dialog.duplicateName') : t('fundGroups.dialog.saveError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainDialogLayout open={open} onOpenChange={(isOpen: boolean) => { if (!isOpen) onClose(); }} title={isEditing ? t('fundGroups.dialog.editTitle') : t('fundGroups.dialog.createTitle')}>
      <div className="flex flex-col gap-4 p-4 min-w-[520px] max-w-[640px]">
        <div className="flex flex-col gap-1">
          <label htmlFor="fg-name" className="body-sm-semibold text-(--font-secondary)">{t('fundGroups.columns.name')}</label>
          <input id="fg-name" type="text" className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular" value={name} onChange={(e) => { setName(e.target.value); if (error) setError(null); }} />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="fg-desc" className="body-sm-semibold text-(--font-secondary)">{t('fundGroups.columns.description')}</label>
          <input id="fg-desc" type="text" className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1">
          <label className="body-sm-semibold text-(--font-secondary)">{t('fundGroups.dialog.funds')}</label>
          <CheckboxMultiSelect options={fundOptions} selected={fundIds} onChange={setFundIds} />
        </div>
        <div className="flex flex-col gap-1">
          <label className="body-sm-semibold text-(--font-secondary)">{t('fundGroups.dialog.users')}</label>
          <CheckboxMultiSelect options={userOptions} selected={userEmails} onChange={setUserEmails} />
        </div>

        {error && <p className="text-red-500 body-sm-regular">{error}</p>}

        <div className="flex gap-3 justify-end mt-2">
          <Button variant="white" onClick={onClose} disabled={isSubmitting}>{t('close')}</Button>
          <Button variant="primary" onClick={handleSave} disabled={isSubmitting}>{isSubmitting ? t('saving') : t('save')}</Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default EditOrCreateFundGroupDialog;
